import React, { useState, useRef, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Avatar, Button, TextField,
    Grid, CircularProgress, Alert, Divider
} from '@mui/material';
import { PhotoCamera, Person } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/axios';

const Profile = () => {
    const { user, checkAuth } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [photoUrl, setPhotoUrl] = useState<string | null>(null);
    const [photoVersion, setPhotoVersion] = useState(0);

    // Fetch profile photo as blob via authenticated API
    useEffect(() => {
        let objectUrl: string | null = null;
        let cancelled = false;

        const fetchPhoto = async () => {
            if (!user?.profile_photo) {
                setPhotoUrl(null);
                return;
            }
            try {
                const response = await api.get('/me/profile-photo/serve', {
                    responseType: 'blob',
                });
                if (!cancelled) {
                    objectUrl = URL.createObjectURL(response.data);
                    setPhotoUrl(objectUrl);
                }
            } catch {
                if (!cancelled) setPhotoUrl(null);
            }
        };

        fetchPhoto();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [user?.profile_photo, photoVersion]);

    const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validation
        if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
            setError('Please upload a valid image file (JPEG, PNG).');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('File size must be less than 5MB.');
            return;
        }

        setError(null);
        setSuccess(null);
        setUploading(true);

        const formData = new FormData();
        formData.append('photo', file);

        try {
            const response = await api.post('/me/profile-photo', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.status === 'success') {
                setSuccess('Profile photo updated successfully!');
                await checkAuth();
                setPhotoVersion(v => v + 1); // Force re-fetch of photo
            } else {
                setError(response.data.message || 'Failed to upload photo.');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to upload photo.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <Box sx={{ maxWidth: 800, margin: '0 auto', p: { xs: 1, md: 3 } }}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
                My Profile
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            <Grid container spacing={4}>
                {/* Profile Photo Section */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ height: '100%', textAlign: 'center', p: 3, boxShadow: 2, borderRadius: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                                <Avatar
                                    src={photoUrl || undefined}
                                    sx={{ width: 150, height: 150, mb: 3, fontSize: '4rem', bgcolor: 'primary.main', border: '4px solid #fff', boxShadow: 3 }}
                                >
                                    {!photoUrl && <Person fontSize="inherit" />}
                                </Avatar>

                                <input
                                    type="file"
                                    accept="image/jpeg, image/png, image/jpg"
                                    hidden
                                    ref={fileInputRef}
                                    onChange={handlePhotoUpload}
                                />
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={uploading ? <CircularProgress size={20} /> : <PhotoCamera />}
                                    disabled={uploading}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ borderRadius: 2 }}
                                >
                                    {uploading ? 'Uploading...' : 'Change Photo'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Personal Information Section */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card sx={{ boxShadow: 2, borderRadius: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                                Profile Details
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Full Name"
                                        value={user?.name || ''}
                                        InputProps={{ readOnly: true }}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        value={user?.phone || ''}
                                        InputProps={{ readOnly: true }}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        value={user?.email || 'N/A'}
                                        InputProps={{ readOnly: true }}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Role"
                                        value={(user?.role || '').toUpperCase()}
                                        InputProps={{ readOnly: true }}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Tenant"
                                        value={user?.tenant?.name || 'Super Admin Organization'}
                                        InputProps={{ readOnly: true }}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Profile;
